import {OfferType} from "../../offer/types/OfferType";
import {IUnlockChoiceConfirmationContext, IUnlockChoiceOption, IUnlockChoiceOptionBadge, UnlockChoiceOptionIcon} from "../types/IUnlockChoiceOption";
import {UnlockChoiceEntryPoint} from "../types/UnlockChoiceEntryPoint";
import {getUnlockChoicePropertyNoun} from "../utils/get_unlock_choice_property_noun";

const FULL_PACKAGE_BADGE: IUnlockChoiceOptionBadge = {text: "Najczęściej wybierane", color: "green"};

const getOfferSubject = ({propertyNumber, offerType}: IUnlockChoiceConfirmationContext) =>
    propertyNumber ? `oferty ${getUnlockChoicePropertyNoun(offerType).genitive} ${propertyNumber}` : "oferty";

const getReservationSubject = ({propertyNumber, offerType}: IUnlockChoiceConfirmationContext) => {
    const {genitive} = getUnlockChoicePropertyNoun(offerType);

    return propertyNumber ? `rezerwacji ${genitive} ${propertyNumber}` : `rezerwacji ${genitive}`;
};

const getFullPackageSubject = ({propertyNumber, offerType}: IUnlockChoiceConfirmationContext) =>
    propertyNumber ? `kompletu informacji o ${getUnlockChoicePropertyNoun(offerType).locative} ${propertyNumber}` : "kompletu informacji o inwestycji";

const getMeetingSubject = ({propertyNumber, offerType}: IUnlockChoiceConfirmationContext) =>
    propertyNumber ? `umówienia spotkania dot. ${getUnlockChoicePropertyNoun(offerType).genitive} ${propertyNumber}` : "umówienia spotkania";

const getQuickPurchaseBadge = (offerType: OfferType | null): IUnlockChoiceOptionBadge => ({
    text: `Chcę szybko kupić ${getUnlockChoicePropertyNoun(offerType).nominative}`,
    color: "beige"
});

const OFFER_OPTION: IUnlockChoiceOption = {
    id: "offer",
    icon: "chat",
    title: "Niezobowiązująca oferta",
    description: "Otrzymasz pełną ofertę zawierającą niepublikowane informacje na stronie. Możemy też zadzwonić, żeby dopytać o Twoje preferencje.",
    badge: {text: "Dopiero się rozglądam", color: "green"},
    ctaText: "Poznaj ofertę",
    sendsLead: true,
    confirmationSubject: getOfferSubject
};

const getOfferSecondOptions = (offerType: OfferType | null) =>
    ({
        reservation: {
            id: "reservation",
            icon: "calendar",
            title: `Bezpłatna rezerwacja ${getUnlockChoicePropertyNoun(offerType).genitive}`,
            description: "Deweloper skontaktuje się z Tobą w sprawie bezpłatnej rezerwacji.",
            badge: getQuickPurchaseBadge(offerType),
            ctaText: "Zarezerwuj bezpłatnie",
            sendsLead: true,
            confirmationSubject: getReservationSubject
        },
        meeting: {
            id: "meeting",
            icon: "calendar",
            title: "Umów spotkanie z deweloperem",
            description: "Deweloper skontaktuje się z Tobą w celu umówienia dogodnego terminu spotkania w biurze sprzedaży.",
            badge: getQuickPurchaseBadge(offerType),
            ctaText: "Umów spotkanie",
            sendsLead: true,
            confirmationSubject: getMeetingSubject
        }
    }) satisfies Record<string, IUnlockChoiceOption>;

// The meeting stands in for the reservation until offer reservations launch - switch back to "reservation" then
const ACTIVE_OFFER_SECOND_OPTION: keyof ReturnType<typeof getOfferSecondOptions> = "meeting";

const getOfferChoiceOptions = (offerType: OfferType | null): [IUnlockChoiceOption, IUnlockChoiceOption] => [
    OFFER_OPTION,
    getOfferSecondOptions(offerType)[ACTIVE_OFFER_SECOND_OPTION]
];

const FULL_PACKAGE_MATERIALS = "pełny pakiet materiałów o tej inwestycji: cennik, harmonogram i standard wykończenia.";

const getFullPackageDescription = (content: string) => `Oprócz ${content} wyślemy Ci ${FULL_PACKAGE_MATERIALS}`;

type ContentUnlockChoiceEntryPoint = Exclude<UnlockChoiceEntryPoint, UnlockChoiceEntryPoint.PRICE | UnlockChoiceEntryPoint.LEAD>;

interface IContentUnlockChoiceOptionsConfig {
    action: string;
    ctaAction: string;
    fullPackageDescription: string;
    contentOnlyIcon: UnlockChoiceOptionIcon;
    contentOnlyTitle: string;
    contentOnlyCtaText: string;
}

const getContentUnlockChoiceOptions = (config: IContentUnlockChoiceOptionsConfig): [IUnlockChoiceOption, IUnlockChoiceOption] => [
    {
        id: "full_package",
        icon: "chat",
        title: `${config.action} i otrzymaj komplet informacji od dewelopera`,
        description: config.fullPackageDescription,
        badge: FULL_PACKAGE_BADGE,
        ctaText: `${config.ctaAction} i poznaj ofertę`,
        sendsLead: true,
        confirmationSubject: getFullPackageSubject
    },
    {
        id: "content_only",
        icon: config.contentOnlyIcon,
        title: config.contentOnlyTitle,
        description: null,
        badge: null,
        ctaText: config.contentOnlyCtaText,
        sendsLead: false
    }
];

const CONTENT_UNLOCK_CHOICE_OPTIONS: Record<ContentUnlockChoiceEntryPoint, [IUnlockChoiceOption, IUnlockChoiceOption]> = {
    [UnlockChoiceEntryPoint.FLOOR_PLAN]: getContentUnlockChoiceOptions({
        action: "Pobierz rzut",
        ctaAction: "Pobierz rzut",
        fullPackageDescription: getFullPackageDescription("rzutu"),
        contentOnlyIcon: "floor_plan",
        contentOnlyTitle: "Pobierz tylko rzut",
        contentOnlyCtaText: "Pobierz rzut"
    }),
    [UnlockChoiceEntryPoint.PRICE_HISTORY]: getContentUnlockChoiceOptions({
        action: "Zobacz historię cen",
        ctaAction: "Zobacz historię cen",
        fullPackageDescription: getFullPackageDescription("historii cen"),
        contentOnlyIcon: "price_history",
        contentOnlyTitle: "Zobacz tylko historię cen",
        contentOnlyCtaText: "Zobacz historię cen"
    }),
    [UnlockChoiceEntryPoint.VENDOR_REPORT]: getContentUnlockChoiceOptions({
        action: "Zobacz raport",
        ctaAction: "Zobacz raport",
        fullPackageDescription: getFullPackageDescription("raportu o deweloperze"),
        contentOnlyIcon: "screen_chart",
        contentOnlyTitle: "Zobacz tylko raport",
        contentOnlyCtaText: "Zobacz raport"
    }),
    [UnlockChoiceEntryPoint.CONSTRUCTION_PHOTOS]: getContentUnlockChoiceOptions({
        action: "Zobacz zdjęcia z budowy",
        ctaAction: "Zobacz zdjęcia",
        fullPackageDescription: getFullPackageDescription("zdjęć z budowy"),
        contentOnlyIcon: "camera",
        contentOnlyTitle: "Zobacz tylko zdjęcia z budowy",
        contentOnlyCtaText: "Zobacz zdjęcia z budowy"
    }),
    [UnlockChoiceEntryPoint.FAQ]: getContentUnlockChoiceOptions({
        action: "Zobacz odpowiedź na pytanie",
        ctaAction: "Zobacz odpowiedź",
        fullPackageDescription: `Wyślemy Ci ${FULL_PACKAGE_MATERIALS}`,
        contentOnlyIcon: "ai_search",
        contentOnlyTitle: "Zobacz odpowiedź na pytanie",
        contentOnlyCtaText: "Zobacz odpowiedź"
    })
};

export const getUnlockChoiceOptions = (entryPoint: UnlockChoiceEntryPoint, offerType: OfferType | null): [IUnlockChoiceOption, IUnlockChoiceOption] => {
    if (entryPoint === UnlockChoiceEntryPoint.PRICE || entryPoint === UnlockChoiceEntryPoint.LEAD) {
        return getOfferChoiceOptions(offerType);
    }

    return CONTENT_UNLOCK_CHOICE_OPTIONS[entryPoint];
};
import {OfferType} from "../../offer/types/OfferType";
import {IUnlockChoiceConfirmationContext, IUnlockChoiceOption, IUnlockChoiceOptionBadge, UnlockChoiceOptionIcon} from "../types/IUnlockChoiceOption";
import {UnlockChoiceEntryPoint} from "../types/UnlockChoiceEntryPoint";
import {getUnlockChoicePropertyNoun} from "../utils/get_unlock_choice_property_noun";

const FULL_PACKAGE_BADGE: IUnlockChoiceOptionBadge = {text: "Najczęściej wybierane", color: "green"};

const getOfferSubject = ({propertyNumber, offerType}: IUnlockChoiceConfirmationContext) =>
    propertyNumber ? `oferty ${getUnlockChoicePropertyNoun(offerType).genitive} ${propertyNumber}` : "oferty";

const getReservationSubject = ({propertyNumber, offerType}: IUnlockChoiceConfirmationContext) => {
    const {genitive} = getUnlockChoicePropertyNoun(offerType);

    return propertyNumber ? `rezerwacji ${genitive} ${propertyNumber}` : `rezerwacji ${genitive}`;
};

const getFullPackageSubject = ({propertyNumber, offerType}: IUnlockChoiceConfirmationContext) =>
    propertyNumber ? `kompletu informacji o ${getUnlockChoicePropertyNoun(offerType).locative} ${propertyNumber}` : "kompletu informacji o inwestycji";

const getMeetingSubject = ({propertyNumber, offerType}: IUnlockChoiceConfirmationContext) =>
    propertyNumber ? `umówienia spotkania dot. ${getUnlockChoicePropertyNoun(offerType).genitive} ${propertyNumber}` : "umówienia spotkania";

const getQuickPurchaseBadge = (offerType: OfferType | null): IUnlockChoiceOptionBadge => ({
    text: `Chcę szybko kupić ${getUnlockChoicePropertyNoun(offerType).nominative}`,
    color: "beige"
});

const OFFER_OPTION: IUnlockChoiceOption = {
    id: "offer",
    icon: "chat",
    title: "Niezobowiązująca oferta",
    description: "Otrzymasz pełną ofertę zawierającą niepublikowane informacje na stronie. Możemy też zadzwonić, żeby dopytać o Twoje preferencje.",
    badge: {text: "Dopiero się rozglądam", color: "green"},
    ctaText: "Poznaj ofertę",
    sendsLead: true,
    confirmationSubject: getOfferSubject
};

const getOfferSecondOptions = (offerType: OfferType | null) =>
    ({
        reservation: {
            id: "reservation",
            icon: "calendar",
            title: `Bezpłatna rezerwacja ${getUnlockChoicePropertyNoun(offerType).genitive}`,
            description: "Deweloper skontaktuje się z Tobą w sprawie bezpłatnej rezerwacji.",
            badge: getQuickPurchaseBadge(offerType),
            ctaText: "Zarezerwuj bezpłatnie",
            sendsLead: true,
            confirmationSubject: getReservationSubject
        },
        meeting: {
            id: "meeting",
            icon: "calendar",
            title: "Umów spotkanie z deweloperem",
            description: "Deweloper skontaktuje się z Tobą w celu umówienia dogodnego terminu spotkania w biurze sprzedaży.",
            badge: getQuickPurchaseBadge(offerType),
            ctaText: "Umów spotkanie",
            sendsLead: true,
            confirmationSubject: getMeetingSubject
        }
    }) satisfies Record<string, IUnlockChoiceOption>;

// The meeting stands in for the reservation until offer reservations launch - switch back to "reservation" then
const ACTIVE_OFFER_SECOND_OPTION: keyof ReturnType<typeof getOfferSecondOptions> = "meeting";

const getOfferChoiceOptions = (offerType: OfferType | null): [IUnlockChoiceOption, IUnlockChoiceOption] => [
    OFFER_OPTION,
    getOfferSecondOptions(offerType)[ACTIVE_OFFER_SECOND_OPTION]
];

const FULL_PACKAGE_MATERIALS = "pełny pakiet materiałów o tej inwestycji: cennik, harmonogram i standard wykończenia.";

const getFullPackageDescription = (content: string) => `Oprócz ${content} wyślemy Ci ${FULL_PACKAGE_MATERIALS}`;

type ContentUnlockChoiceEntryPoint = Exclude<UnlockChoiceEntryPoint, UnlockChoiceEntryPoint.PRICE | UnlockChoiceEntryPoint.LEAD>;

interface IContentUnlockChoiceOptionsConfig {
    action: string;
    ctaAction: string;
    fullPackageDescription: string;
    contentOnlyIcon: UnlockChoiceOptionIcon;
    contentOnlyTitle: string;
    contentOnlyCtaText: string;
}

const getContentUnlockChoiceOptions = (config: IContentUnlockChoiceOptionsConfig): [IUnlockChoiceOption, IUnlockChoiceOption] => [
    {
        id: "full_package",
        icon: "chat",
        title: `${config.action} i otrzymaj komplet informacji od dewelopera`,
        description: config.fullPackageDescription,
        badge: FULL_PACKAGE_BADGE,
        ctaText: `${config.ctaAction} i poznaj ofertę`,
        sendsLead: true,
        confirmationSubject: getFullPackageSubject
    },
    {
        id: "content_only",
        icon: config.contentOnlyIcon,
        title: config.contentOnlyTitle,
        description: null,
        badge: null,
        ctaText: config.contentOnlyCtaText,
        sendsLead: false
    }
];

const CONTENT_UNLOCK_CHOICE_OPTIONS: Record<ContentUnlockChoiceEntryPoint, [IUnlockChoiceOption, IUnlockChoiceOption]> = {
    [UnlockChoiceEntryPoint.FLOOR_PLAN]: getContentUnlockChoiceOptions({
        action: "Pobierz rzut",
        ctaAction: "Pobierz rzut",
        fullPackageDescription: getFullPackageDescription("rzutu"),
        contentOnlyIcon: "floor_plan",
        contentOnlyTitle: "Pobierz tylko rzut",
        contentOnlyCtaText: "Pobierz rzut"
    }),
    [UnlockChoiceEntryPoint.PRICE_HISTORY]: getContentUnlockChoiceOptions({
        action: "Zobacz historię cen",
        ctaAction: "Zobacz historię cen",
        fullPackageDescription: getFullPackageDescription("historii cen"),
        contentOnlyIcon: "price_history",
        contentOnlyTitle: "Zobacz tylko historię cen",
        contentOnlyCtaText: "Zobacz historię cen"
    }),
    [UnlockChoiceEntryPoint.VENDOR_REPORT]: getContentUnlockChoiceOptions({
        action: "Zobacz raport",
        ctaAction: "Zobacz raport",
        fullPackageDescription: getFullPackageDescription("raportu o deweloperze"),
        contentOnlyIcon: "screen_chart",
        contentOnlyTitle: "Zobacz tylko raport",
        contentOnlyCtaText: "Zobacz raport"
    }),
    [UnlockChoiceEntryPoint.CONSTRUCTION_PHOTOS]: getContentUnlockChoiceOptions({
        action: "Zobacz zdjęcia z budowy",
        ctaAction: "Zobacz zdjęcia",
        fullPackageDescription: getFullPackageDescription("zdjęć z budowy"),
        contentOnlyIcon: "camera",
        contentOnlyTitle: "Zobacz tylko zdjęcia z budowy",
        contentOnlyCtaText: "Zobacz zdjęcia z budowy"
    }),
    [UnlockChoiceEntryPoint.FAQ]: getContentUnlockChoiceOptions({
        action: "Zobacz odpowiedź na pytanie",
        ctaAction: "Zobacz odpowiedź",
        fullPackageDescription: `Wyślemy Ci ${FULL_PACKAGE_MATERIALS}`,
        contentOnlyIcon: "ai_search",
        contentOnlyTitle: "Zobacz odpowiedź na pytanie",
        contentOnlyCtaText: "Zobacz odpowiedź"
    })
};

export const getUnlockChoiceOptions = (entryPoint: UnlockChoiceEntryPoint, offerType: OfferType | null): [IUnlockChoiceOption, IUnlockChoiceOption] => {
    if (entryPoint === UnlockChoiceEntryPoint.PRICE || entryPoint === UnlockChoiceEntryPoint.LEAD) {
        return getOfferChoiceOptions(offerType);
    }

    return CONTENT_UNLOCK_CHOICE_OPTIONS[entryPoint];
};
